import Utils from "models/helper/Utils";
import SequencerNodeFactory from "models/audioEditor/sequencerNodes/SequencerNodeFactory";

/**
 * @typedef {Object} AIEditSuggestionsInput
 * @property {Object} results - The AI analysis results
 * @property {Object} idMapping - Mapping of IDs
 * @property {Object} [initialRequest] - Original request data including customDeletableTypes
 */

/**
 * @typedef {Object} AIEditSuggestionsData
 * @property {Object} results - The AI analysis results  
 * @property {Object} idMapping - Mapping of IDs
 * @property {Object} [initialRequest] - Original request data
 */

/**
 * @typedef {Object} AIEditSuggestionsOutput
 * @property {Object} sequencerNodeIdsToGroup - Mapping of sequencer node IDs to group info
 * @property {AIEditSuggestionsData} aiSuggestions - Complete AI suggestions data
 */

const AIEditSuggestionsProcessor = {
    
    /**
     * Initialize a group and sequencer node array with the given parameters
     */
    initializeGroupAndSequencerNodeArray: function(reason, confidence, uid, recommendation) {
        const sequencerNodes = [];
        let group = {
            sequencerNodes, 
            reason, 
            confidence, 
            uid, 
            recommendation, 
            streamlinedSuggestionConfidence: Utils.getInstance().createStreamlineSuggestionConfidence(confidence, recommendation)
        };
        return { sequencerNodes, group };
    },

    /**
     * Check if a sequencer node was part of the analysis
     */
    wasSequencerNodePartOfTheAnalysis: function(sequencerNode, analyzedNodeCIds) {
        return analyzedNodeCIds.includes(sequencerNode.cid);
    },

    /**
     * Check if a suggestion type is a custom deletable type
     */
    isCustomDeletableType: function(suggestionType, customDeletableTypes) {
        return !!customDeletableTypes[suggestionType];
    },

    /**
     * Create highlighted group info object
     */
    createHighlightedGroupInfo: function(group, confidence, recommendation, suggestionType, uid, shouldContinueMatching, customDeletableTypes) {
        const isCustomType = this.isCustomDeletableType(suggestionType, customDeletableTypes);
        const customTypeLabel = isCustomType ? customDeletableTypes[suggestionType].label : null;
        return {
            group, 
            confidence, 
            recommendation, 
            type: suggestionType, 
            isCustomType, 
            customTypeLabel, 
            uid, 
            shouldContinueMatching, 
            streamlinedSuggestionConfidence: Utils.getInstance().createStreamlineSuggestionConfidence(confidence, recommendation)
        };
    },

    /**
     * Add subgroup ID to group UID
     */
    addSubgroupIdToGroupUID: function(groupInfo, subGroupId) {
        groupInfo.uid = groupInfo.uid + `[${subGroupId}]`;
        if (groupInfo.group && groupInfo.group.uid) {
            groupInfo.group.uid = groupInfo.group.uid + `[${subGroupId}]`;
        }
    },

    /**
     * Check if sequencer node is part of the highlighted group
     */
    isSequencerNodePartOfTheHighlightedGroup: function(sequencerNode, sortedSequencerNodes) {
        return sortedSequencerNodes.includes(sequencerNode);
    },

    /**
     * Process AI edit suggestions and return sequencer node IDs to group mapping
     * 
     * @param {AIEditSuggestionsInput} aiSuggestionsData - The AI suggestions data from the API
     * @param {Object} options - Configuration options
     * @param {Object} callbacks - Callback functions that need Sequencer context
     * @returns {AIEditSuggestionsOutput} Object containing sequencerNodeIdsToGroup and aiSuggestions
     */
    processAIEditSuggestions: function(aiSuggestionsData, options, callbacks) {
        aiSuggestionsData = aiSuggestionsData || {};
        const { results = {}, idMapping = {}, initialRequest } = aiSuggestionsData;
        const customDeletableTypes = initialRequest?.customDeletableTypes || {};
        const sequencerNodeIdsToGroupMapping = {};
        const NO_RESULT_TYPE = "";
        
        // Get configuration from options
        const {
            allowedConfidence,
            allowedTypes,
            allowedValidationLevels,
            projectIdReferenceForSuggestions,
            strictMode = true
        } = options;

        // Get callbacks
        const {
            getSequencerNodeByNodeCid,
            getSequencerNodeFromProject,
            getSequencerNodeIndex
        } = callbacks;

        const findNearestAvailableAnchor = function(startNode, preferInsertBefore) {
            if (!startNode) return null;

            const searchDirections = [
                { step: preferInsertBefore ? 'previous' : 'next', insertBefore: preferInsertBefore },
                { step: preferInsertBefore ? 'next' : 'previous', insertBefore: !preferInsertBefore }
            ];

            for (let directionIndex = 0; directionIndex < searchDirections.length; directionIndex++) {
                const direction = searchDirections[directionIndex];
                let candidate = startNode;
                let attempts = 0;

                while (candidate && attempts < 50) {
                    candidate = direction.step === 'previous' ? candidate.getPrevious() : candidate.getNext();
                    if (!candidate) break;
                    if (!sequencerNodeIdsToGroupMapping[candidate.cid]) {
                        return {
                            anchorNode: candidate,
                            insertBefore: direction.insertBefore,
                            walkedOppositeDirection: direction.insertBefore !== preferInsertBefore
                        };
                    }
                    attempts++;
                }
            }

            return null;
        };

        // Prepare data structures
        const resultTypeToResultsMap = {};
        const hasIdMapping = Object.keys(idMapping || {}).length > 0;
        let analyzedProjectSequencerNodeCIds = hasIdMapping ? Utils.getInstance().flattenIdsFromVerbatimTranscriptionWithIds(Object.values(idMapping || {})) : [];
        
        if (hasIdMapping && projectIdReferenceForSuggestions) {
            analyzedProjectSequencerNodeCIds = SequencerNodeFactory.getInstance().getSequencerNodeIdsUsingNodeCidFromProject(
                analyzedProjectSequencerNodeCIds, 
                projectIdReferenceForSuggestions, 
                callbacks.sequencerInstance
            );
        }

        // Organize results by type
        if (hasIdMapping) {
            if (results && results.groupedIds) {
                resultTypeToResultsMap[NO_RESULT_TYPE] = Array.isArray(results.groupedIds) ? { groupedIds: results.groupedIds } : results.groupedIds;
            } else if (results) {
                for (const [resultCategory, categoryData] of Object.entries(results)) {
                    resultTypeToResultsMap[resultCategory] = Array.isArray(categoryData) ? { groupedIds: categoryData } : categoryData;
                }
            }
        }

        // Process each result type
        for (const [resultType, resultData] of Object.entries(resultTypeToResultsMap)) {
            if (!resultData || !Array.isArray(resultData.groupedIds)) {
                continue;
            }
            for (let { ids, reason, confidence, wasDeletionApplied, wasDirectlyApplied, uid, evaluation, isValid, programmaticValidation } of resultData.groupedIds) {
                
                // Check if this result should be processed
                if (programmaticValidation && 
                    isValid && 
                    Utils.getInstance().isAcceptedEvaluation(evaluation, allowedValidationLevels) && 
                    wasDeletionApplied && 
                    wasDirectlyApplied && 
                    allowedConfidence.includes(confidence) && 
                    ((resultType === NO_RESULT_TYPE) || allowedTypes.includes(resultType) || this.isCustomDeletableType(resultType, customDeletableTypes))) {
                    
                    let { sequencerNodes, group } = this.initializeGroupAndSequencerNodeArray(reason, confidence, uid, evaluation?.recommendation);

                    // Get sequencer nodes for the IDs
                    const nodeIdToSequencerNodeMap = {};
                    for (let nodeId of Utils.getInstance().flattenIdsFromVerbatimTranscriptionWithIds(ids)) {
                        nodeIdToSequencerNodeMap[nodeId] = projectIdReferenceForSuggestions ? 
                            getSequencerNodeFromProject(nodeId, projectIdReferenceForSuggestions) : 
                            getSequencerNodeByNodeCid(nodeId);
                    }

                    const sortedSequencerNodes = Object.values(nodeIdToSequencerNodeMap)
                        .sort((nodeA, nodeB) => getSequencerNodeIndex(nodeA) - getSequencerNodeIndex(nodeB));
                    
                    let currentNode = sortedSequencerNodes[0];
                    let shouldContinueMatching = true;
                    const lastNodeInGroup = sortedSequencerNodes[sortedSequencerNodes.length - 1];
                    let subgroupCounter = 1;
                    
                    while (currentNode) {
                        if (strictMode) {
                            // Strict mode - only match nodes that are part of the highlighted group
                            if (this.isSequencerNodePartOfTheHighlightedGroup(currentNode, sortedSequencerNodes) || 
                                (shouldContinueMatching && !this.wasSequencerNodePartOfTheAnalysis(currentNode, analyzedProjectSequencerNodeCIds))) {
                                
                                sequencerNodes.push(currentNode);
                                shouldContinueMatching = true;
                                sequencerNodeIdsToGroupMapping[currentNode.cid] = this.createHighlightedGroupInfo(
                                    group, 
                                    confidence, 
                                    evaluation?.recommendation, 
                                    (resultType === NO_RESULT_TYPE) ? null : resultType, 
                                    uid, 
                                    true, 
                                    customDeletableTypes
                                );

                                if (currentNode === lastNodeInGroup) {
                                    const groupInfo = sequencerNodeIdsToGroupMapping[currentNode.cid];
                                    if (groupInfo) {
                                        groupInfo.shouldContinueMatching = false;
                                    }
                                    shouldContinueMatching = false;
                                    currentNode = null;
                                    break;
                                }
                            } else {
                                const previousSequencerNode = currentNode.getPrevious();
                                if (shouldContinueMatching && previousSequencerNode && sequencerNodeIdsToGroupMapping[previousSequencerNode.cid]) {
                                    // End current group and start a new one
                                    const previousGroupInfo = sequencerNodeIdsToGroupMapping[previousSequencerNode.cid];
                                    if (previousGroupInfo && previousGroupInfo.shouldContinueMatching) {
                                        previousGroupInfo.shouldContinueMatching = false;
                                        this.addSubgroupIdToGroupUID(previousGroupInfo, subgroupCounter);
                                        subgroupCounter++;
                                        
                                        // Create new group for continuing nodes
                                        const newGroupResult = this.initializeGroupAndSequencerNodeArray(reason, confidence, uid, evaluation?.recommendation);
                                        sequencerNodes = newGroupResult.sequencerNodes;
                                        group = newGroupResult.group;
                                    }
                                }
                                shouldContinueMatching = false;
                            }
                        } else {
                            // Not in strict mode - match until reaching the last node
                            sequencerNodes.push(currentNode);
                            shouldContinueMatching = true;
                            sequencerNodeIdsToGroupMapping[currentNode.cid] = this.createHighlightedGroupInfo(
                                group, 
                                confidence, 
                                evaluation?.recommendation, 
                                (resultType === NO_RESULT_TYPE) ? null : resultType, 
                                uid, 
                                true, 
                                customDeletableTypes
                            );

                            if (currentNode === lastNodeInGroup) {
                                const groupInfo = sequencerNodeIdsToGroupMapping[currentNode.cid];
                                if (groupInfo) {
                                    groupInfo.shouldContinueMatching = false;
                                }
                                shouldContinueMatching = false;
                                currentNode = null;
                                break;
                            }
                        }

                        currentNode = currentNode.getNext();
                        if (!currentNode && process.env.NODE_ENV === 'development') {
                            console.log("reached the end of the nodes");
                        }
                    }
                }
            }
        }

        // ─── Process addition suggestions (ELM v2) ────────────────────────
        // Addition suggestions are processed AFTER deletions so we can avoid
        // anchoring to nodes that are part of deletion groups.
        const additionSuggestions = options.additionSuggestions || [];
        console.log('[ELM-TRACE] Total additionSuggestions received:', additionSuggestions.length, additionSuggestions.map(a => ({ type: a.type, positionResolution: a.positionResolution, validationVerdict: a.validationVerdict, insertAfterNodeCid: a.insertAfterNodeCid ? a.insertAfterNodeCid.substring(0,20) : null })));
        for (let i = 0; i < additionSuggestions.length; i++) {
            const addition = additionSuggestions[i];

            // Skip suggestions the LLM evaluator rejected
            if (addition.validationVerdict === 'remove') {
                console.log('[ELM-TRACE] Addition[' + i + '] SKIPPED: validationVerdict=remove type=' + addition.type);
                continue;
            }

            // Resolve anchor node — try insertAfterNodeCid first, then insertBeforeNodeCid
            // LLM may return pipe-separated CIDs (e.g. "c123|c124|c125") — try each one.
            let anchorNode = null;
            let insertBefore = false;
            const isSemanticResolution = addition.positionResolution === 'semantic';

            // Helper: resolve a CID string that may contain pipes
            const resolveMultiCid = function(cidStr) {
                if (!cidStr) return null;
                const candidates = cidStr.split('|');
                for (var c = 0; c < candidates.length; c++) {
                    var node = getSequencerNodeByNodeCid(candidates[c].trim());
                    if (node) return node;
                }
                return null;
            };

            if (addition.insertAfterNodeCid) {
                anchorNode = resolveMultiCid(addition.insertAfterNodeCid);
                insertBefore = false;
            }
            if (!anchorNode && addition.insertBeforeNodeCid) {
                anchorNode = resolveMultiCid(addition.insertBeforeNodeCid);
                insertBefore = true;
            }

            // Semantic resolution: null insertAfterCid means "insert at very beginning".
            // End-of-transcript additions always have insertAfterNodeCid = lastCid, which resolves
            // successfully above — so if we reach here with !anchorNode it means the CID was broken.
            // Only apply the beginning-of-transcript fallback; broken CIDs fall through to the skip.
            if (isSemanticResolution && !anchorNode) {
                if (!addition.insertAfterNodeCid && addition.insertBeforeNodeCid) {
                    // Explicitly null insertAfterCid — "before the first word"
                    anchorNode = callbacks.sequencerInstance.getFirstNode();
                    insertBefore = true;
                }
                // If insertAfterNodeCid was set but failed to resolve it's a broken CID.
                // Do NOT fall back to getLastNode() — that would silently misplace the addition.
                // Fall through to the skip path at the else-if below.
            }

            // For v1 (rule-based) positioning: override CIDs using type/positionType heuristics.
            // For v2 (semantic) positioning: trust the Lambda's CIDs — they were resolved by LLM.
            if (!isSemanticResolution) {
                const pos = (addition.positionType || '').toLowerCase();
                const additionType = (addition.type || '').toLowerCase();
                if (/end|outro|closing/.test(pos) || /outro/.test(additionType)) {
                    anchorNode = callbacks.sequencerInstance.getLastNode();
                    insertBefore = false;
                } else if (!anchorNode) {
                    if (/begin|start|intro/.test(pos)) {
                        anchorNode = callbacks.sequencerInstance.getFirstNode();
                        insertBefore = true;
                    } else {
                        console.log('[ELM-TRACE] Addition[' + i + '] SKIPPED: non-semantic positionType mismatch type=' + addition.type + ' positionType=' + addition.positionType + ' positionResolution=' + addition.positionResolution);
                        continue;
                    }
                }
            } else if (!anchorNode) {
                // Semantic resolution but CID didn't resolve — skip this addition
                console.log('[ELM-TRACE] Addition[' + i + '] SKIPPED: semantic CID not found type=' + addition.type + ' insertAfterCid=' + addition.insertAfterNodeCid + ' insertBeforeCid=' + addition.insertBeforeNodeCid);
                continue;
            }

            if (!anchorNode) { console.log('[ELM-TRACE] Addition[' + i + '] SKIPPED: no anchor type=' + addition.type); continue; }

            // If the resolved anchor is already occupied, walk to the nearest
            // surviving node so each addition still gets a real anchor CID.
            if (sequencerNodeIdsToGroupMapping[anchorNode.cid]) {
                const existing = sequencerNodeIdsToGroupMapping[anchorNode.cid];
                const resolvedAnchor = findNearestAvailableAnchor(anchorNode, insertBefore);
                if (resolvedAnchor) {
                    anchorNode = resolvedAnchor.anchorNode;
                    insertBefore = resolvedAnchor.insertBefore;
                    if (resolvedAnchor.walkedOppositeDirection) {
                        console.log('[ELM-TRACE] Addition[' + i + '] anchor resolved via opposite direction walk');
                    }
                } else {
                    const collisionType = existing?.isAddition ? 'another addition' : 'a deletion group';
                    console.log('[ELM-TRACE] Addition[' + i + '] SKIPPED: no surviving neighbor for anchor ' + anchorNode.cid + ' after colliding with ' + collisionType);
                    continue;
                }
            }

            // For wrap additions, resolve the end anchor from wrapNodeCidRange
            let wrapEndNode = null;
            if (addition.wrapNodeCidRange && addition.wrapNodeCidRange.endCid) {
                wrapEndNode = getSequencerNodeByNodeCid(addition.wrapNodeCidRange.endCid);

                // Walk away from deletion groups (same as primary anchor logic)
                if (wrapEndNode && sequencerNodeIdsToGroupMapping[wrapEndNode.cid]) {
                    let neighbor = wrapEndNode.getNext();
                    let attempts = 0;
                    while (neighbor && sequencerNodeIdsToGroupMapping[neighbor.cid] && attempts < 20) {
                        neighbor = neighbor.getNext();
                        attempts++;
                    }
                    if (neighbor && !sequencerNodeIdsToGroupMapping[neighbor.cid]) {
                        wrapEndNode = neighbor;
                    } else {
                        wrapEndNode = null; // will fallback to last node in apply
                    }
                }

                // Edge case: both CIDs resolve to same node — walk forward
                if (wrapEndNode && wrapEndNode === anchorNode) {
                    let walkNode = anchorNode;
                    let lastValid = null;
                    let walkCount = 0;
                    const maxWalk = 50;
                    while (walkCount < maxWalk) {
                        const next = walkNode.getNext();
                        if (!next) break;
                        walkNode = next;
                        lastValid = walkNode;
                        walkCount++;
                    }
                    wrapEndNode = lastValid;
                }
            }

            // Map numeric confidence (0-1) to string level used by the deletion pipeline
            const numConf = typeof addition.confidence === 'number' ? addition.confidence : 0.0;
            const confidenceLevel = numConf >= 0.8 ? 'high' : numConf >= 0.5 ? 'medium' : 'low';

            const uid = 'addition_' + (addition.type || 'audio') + '_' + i;
            const { sequencerNodes, group } = this.initializeGroupAndSequencerNodeArray(
                addition.reasoning || addition.rationale || '',
                confidenceLevel,
                uid,
                'ADD'
            );

            sequencerNodes.push(anchorNode);

            // Guard against any late collision by reassigning to another real node CID.
            if (sequencerNodeIdsToGroupMapping[anchorNode.cid]) {
                const resolvedAnchor = findNearestAvailableAnchor(anchorNode, insertBefore);
                if (!resolvedAnchor) {
                    console.log('[ELM-TRACE] Addition[' + i + '] SKIPPED: no unique anchor remained after late collision check');
                    continue;
                }

                anchorNode = resolvedAnchor.anchorNode;
                insertBefore = resolvedAnchor.insertBefore;
                sequencerNodes[sequencerNodes.length - 1] = anchorNode;
                if (resolvedAnchor.walkedOppositeDirection) {
                    console.log('[ELM-TRACE] Addition[' + i + '] reassigned to unique anchor via opposite direction walk: ' + anchorNode.cid);
                } else {
                    console.log('[ELM-TRACE] Addition[' + i + '] reassigned to unique anchor: ' + anchorNode.cid);
                }
            }
            sequencerNodeIdsToGroupMapping[anchorNode.cid] = {
                group,
                confidence: confidenceLevel,
                recommendation: 'ADD',
                type: addition.type || addition.category || 'unknown',
                isAddition: true,
                additionData: { ...addition, resolvedInsertBefore: insertBefore, resolvedAnchorCid: anchorNode.cid, wrapEndAnchorNode: wrapEndNode || null },
                isCustomType: true,
                customTypeLabel: addition.typeLabel || addition.audioClassification?.description || null,
                positionLabel: addition.positionLabel || null,
                uid,
                shouldContinueMatching: false,
                streamlinedSuggestionConfidence: Utils.getInstance().createStreamlineSuggestionConfidence(confidenceLevel, 'ADD')
            };
        }

        // Use object spreading to preserve ALL original fields automatically
        // This prevents accidentally dropping fields like initialRequest
        return {
            sequencerNodeIdsToGroup: sequencerNodeIdsToGroupMapping,
            aiSuggestions: { ...aiSuggestionsData }
        };
    }
};

export default AIEditSuggestionsProcessor;
